import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';

interface FilterState {
  mainCategory: string;
  brand: string;
  cpuSeries: string;
  cpuModel: string;
  gpu: string;
  ram: string;
  storage: string;
  priceRange: string;
  condition: string;
  location: string;
  sortBy: string;
}

const Index_login: React.FC = () => {
  const navigate = useNavigate();
  const { login, logout, isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    mainCategory: 'desktop',
    brand: 'all',
    cpuSeries: '',
    cpuModel: '',
    gpu: '',
    ram: '',
    storage: '',
    priceRange: '',
    condition: '',
    location: '',
    sortBy: 'newest'
  });

  useEffect(() => {
    // Check if user is already authenticated
    if (!isAuthenticated) {
      // If not authenticated, stay on login page
      return;
    }
  }, [isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(email, password);
      // Navigation happens in the login function in AuthContext
    } catch (error) {
      setError('Giriş başarısız. Lütfen bilgilerinizi kontrol edin.');
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-vh-100 bg-light">
        <header className="header sticky-top bg-white">
          <nav className="navbar navbar-expand-lg">
            <div className="container">
              <Link className="navbar-brand fw-bold text-primary" to="/">
                <i className="fas fa-laptop me-2" />
                SadeceBilgisayar.com
              </Link>
            </div>
          </nav>
        </header>

        <main className="container py-5">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <h2 className="text-center mb-4">Giriş Yap</h2>
                  {error && <div className="alert alert-danger">{error}</div>}
                  <form onSubmit={handleLogin}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label">E-posta</label>
                      <input
                        type="email"
                        className="form-control"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="password" className="form-label">Şifre</label>
                      <input
                        type="password"
                        className="form-control"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    <Button type="submit" className="btn btn-primary w-100 mb-3">
                      Giriş Yap
                    </Button>
                    <div className="text-center">
                      <Link to="/kayit" className="text-decoration-none">
                        Hesabınız yok mu? Kayıt olun
                      </Link>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <header className="header sticky-top bg-white">
        <nav className="navbar navbar-expand-lg">
          <div className="container">
            {/* Logo */}
            <Link className="navbar-brand fw-bold text-primary" to="/">
              <i className="fas fa-laptop me-2" />
              SadeceBilgisayar.com
            </Link>

            {/* Arama Kutusu */}
            <div className="search-box mx-lg-4 flex-grow-1">
              <form className="d-flex">
                <div className="input-group">
                  <input
                    aria-label="Arama"
                    className="form-control"
                    placeholder="Dizüstü, masaüstü, parça ara..."
                    type="search"
                  />
                  <Button className="btn btn-primary" type="submit">
                    <i className="fas fa-search" />
                  </Button>
                </div>
              </form>
            </div>

            {/* Sağ Menü */}
            <div className="nav-right d-flex align-items-center">
              <Link className="btn btn-outline-primary me-3" to="/ilan-ver">
                <i className="fas fa-plus-circle me-2" />
                İlan Ver
              </Link>

              {/* Bildirimler */}
              <div className="dropdown me-3">
                <Button
                  className="btn btn-link text-dark position-relative"
                  data-bs-toggle="dropdown"
                >
                  <i className="fas fa-bell fs-5" />
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    3
                  </span>
                </Button>
                <div className="dropdown-menu dropdown-menu-end">
                  <h6 className="dropdown-header">Bildirimler</h6>
                  <Link className="dropdown-item" to="#">
                    İlanınıza yeni bir mesaj geldi
                  </Link>
                  <Link className="dropdown-item" to="#">
                    Takip ettiğiniz ürünün fiyatı düştü
                  </Link>
                  <Link className="dropdown-item" to="#">
                    Yeni bir teklif aldınız
                  </Link>
                </div>
              </div>

              {/* Mesajlar */}
              <div className="dropdown me-3">
                <Button
                  className="btn btn-link text-dark position-relative"
                  data-bs-toggle="dropdown"
                >
                  <i className="fas fa-envelope fs-5" />
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    2
                  </span>
                </Button>
                <div className="dropdown-menu dropdown-menu-end">
                  <h6 className="dropdown-header">Mesajlar</h6>
                  <Link className="dropdown-item" to="/mesajlar">
                    Tüm mesajları görüntüle
                  </Link>
                </div>
              </div>

              {/* Kullanıcı Menüsü */}
              <div className="dropdown">
                <Button
                  className="btn btn-link text-dark dropdown-toggle d-flex align-items-center"
                  data-bs-toggle="dropdown"
                >
                  <img
                    alt="Kullanıcı"
                    className="rounded-circle me-2"
                    height="32"
                    src="assets/images/202329115120__P2090010_.jpg"
                    width="32"
                  />
                  <span>Ahmet Yılmaz</span>
                </Button>
                <ul className="dropdown-menu dropdown-menu-end">
                  <li>
                    <Link className="dropdown-item" to="/profil">
                      <i className="fas fa-user me-2" />
                      Profilim
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/ilanlarim">
                      <i className="fas fa-list me-2" />
                      İlanlarım
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item" to="/favoriler">
                      <i className="fas fa-heart me-2" />
                      Favorilerim
                    </Link>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <Button className="dropdown-item" onClick={handleLogout}>
                      <i className="fas fa-sign-out-alt me-2" />
                      Çıkış Yap
                    </Button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </nav>
      </header>

      {/* Ana İçerik */}
      <main className="container py-4">
        <div className="row">
          {/* Sol Filtre Bölümü */}
          <div className="col-lg-3">
            {/* Teknik Destek Butonu */}
            <Link className="btn btn-primary w-100 mb-2" to="/teknik-destek">
              <i className="fas fa-tools me-2" />
              Teknik Destek
            </Link>

            {/* Gönderiler Butonu */}
            <Link className="btn btn-primary w-100 mb-4" to="/gonderiler">
              <i className="fas fa-video me-2" />
              Gönderiler
            </Link>

            {/* Filtreler Kartı */}
            <div className="card mb-4">
              <div className="card-body">
                {/* Kategori Filtresi */}
                <div className="mb-4">
                  <label className="form-label fw-bold">Bilgisayar</label>
                  <div className="category-tree">
                    {/* Ana Kategori Seçimi */}
                    <div className="form-group mb-3">
                      <select
                        className="form-select form-select-sm"
                        name="mainCategory"
                        value={filters.mainCategory}
                        onChange={handleFilterChange}
                      >
                        <option value="desktop">Masaüstü Bilgisayar</option>
                        <option value="laptop">Dizüstü Bilgisayar</option>
                        <option value="allinone">All-in-One PC</option>
                        <option value="ready">Hazır Sistem</option>
                      </select>
                    </div>

                    {/* Alt Kategoriler */}
                    <div className="sub-categories">
                      {/* Marka Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">Marka</label>
                        <select
                          className="form-select form-select-sm"
                          name="brand"
                          value={filters.brand}
                          onChange={handleFilterChange}
                        >
                          <option value="all">Tüm Markalar</option>
                          <option value="asus">Asus</option>
                          <option value="msi">MSI</option>
                          <option value="hp">HP</option>
                          <option value="lenovo">Lenovo</option>
                          <option value="dell">Dell</option>
                          <option value="apple">Apple</option>
                          <option value="acer">Acer</option>
                          <option value="monster">Monster</option>
                          <option value="casper">Casper</option>
                          <option value="samsung">Samsung</option>
                          <option value="toshiba">Toshiba</option>
                          <option value="alienware">Alienware</option>
                          <option value="gigabyte">Gigabyte</option>
                          <option value="grundig">Grundig</option>
                          <option value="hometech">Home Tech</option>
                          <option value="fujitsu">Fujitsu</option>
                          <option value="lg">LG</option>
                          <option value="packardbell">Packard Bell</option>
                          <option value="sony">Sony</option>
                          <option value="other">Diğer</option>
                        </select>
                      </div>

                      {/* İşlemci Serisi Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">İşlemci Serisi</label>
                        <select
                          className="form-select form-select-sm"
                          name="cpuSeries"
                          value={filters.cpuSeries}
                          onChange={handleFilterChange}
                        >
                          <option value="">İşlemci Serisi Seçin</option>
                          <optgroup label="Intel">
                            <option value="intel-core2">Intel Core 2 Quad</option>
                            <option value="intel-extreme">Intel Extreme</option>
                          </optgroup>
                        </select>
                      </div>

                      {/* İşlemci Modeli Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">İşlemci Modeli</label>
                        <select
                          className="form-select form-select-sm"
                          name="cpuModel"
                          value={filters.cpuModel}
                          onChange={handleFilterChange}
                          disabled={!filters.cpuSeries}
                        >
                          <option value="">Önce İşlemci Serisi Seçin</option>
                        </select>
                      </div>

                      {/* Ekran Kartı Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">Ekran Kartı</label>
                        <select
                          className="form-select form-select-sm"
                          name="gpu"
                          value={filters.gpu}
                          onChange={handleFilterChange}
                        >
                          <option value="">Ekran Kartı Seçin</option>
                          <optgroup label="NVIDIA">
                            <option value="nvidia-9800-512">GTS 9800 512MB GDDR3</option>
                            <option value="nvidia-9800-1gb">GTS 9800 1GB GDDR3</option>
                            <option value="nvidia-285">GTX 285 1GB GDDR3</option>
                          </optgroup>
                        </select>
                      </div>

                      {/* RAM Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">RAM</label>
                        <select
                          className="form-select form-select-sm"
                          name="ram"
                          value={filters.ram}
                          onChange={handleFilterChange}
                        >
                          <option value="">RAM Seçin</option>
                          <option value="2gb">2 GB</option>
                          <option value="4gb">4 GB</option>
                          <option value="8gb">8 GB</option>
                          <option value="16gb">16 GB</option>
                          <option value="32gb">32 GB</option>
                          <option value="64gb">64 GB</option>
                        </select>
                      </div>

                      {/* Depolama Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">Depolama</label>
                        <select
                          className="form-select form-select-sm"
                          name="storage"
                          value={filters.storage}
                          onChange={handleFilterChange}
                        >
                          <option value="">Depolama Seçin</option>
                          <option value="128gb">128 GB</option>
                          <option value="256gb">256 GB</option>
                          <option value="512gb">512 GB</option>
                          <option value="1tb">1 TB</option>
                          <option value="2tb">2 TB</option>
                          <option value="4tb">4 TB</option>
                        </select>
                      </div>

                      {/* Fiyat Aralığı Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">Fiyat Aralığı</label>
                        <select
                          className="form-select form-select-sm"
                          name="priceRange"
                          value={filters.priceRange}
                          onChange={handleFilterChange}
                        >
                          <option value="">Fiyat Aralığı Seçin</option>
                          <option value="0-1000">0 - 1.000 TL</option>
                          <option value="1000-2000">1.000 - 2.000 TL</option>
                          <option value="2000-3000">2.000 - 3.000 TL</option>
                          <option value="3000-5000">3.000 - 5.000 TL</option>
                          <option value="5000-10000">5.000 - 10.000 TL</option>
                          <option value="10000+">10.000 TL ve üzeri</option>
                        </select>
                      </div>

                      {/* Durum Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">Durum</label>
                        <select
                          className="form-select form-select-sm"
                          name="condition"
                          value={filters.condition}
                          onChange={handleFilterChange}
                        >
                          <option value="">Durum Seçin</option>
                          <option value="new">Sıfır</option>
                          <option value="used">İkinci El</option>
                        </select>
                      </div>

                      {/* Konum Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">Konum</label>
                        <select
                          className="form-select form-select-sm"
                          name="location"
                          value={filters.location}
                          onChange={handleFilterChange}
                        >
                          <option value="">Konum Seçin</option>
                          <option value="istanbul">İstanbul</option>
                          <option value="ankara">Ankara</option>
                          <option value="izmir">İzmir</option>
                          <option value="bursa">Bursa</option>
                          <option value="antalya">Antalya</option>
                        </select>
                      </div>

                      {/* Sıralama Seçimi */}
                      <div className="form-group mb-3">
                        <label className="form-label small">Sıralama</label>
                        <select
                          className="form-select form-select-sm"
                          name="sortBy"
                          value={filters.sortBy}
                          onChange={handleFilterChange}
                        >
                          <option value="newest">En Yeni</option>
                          <option value="price-asc">Fiyat (Artan)</option>
                          <option value="price-desc">Fiyat (Azalan)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sağ İçerik Bölümü */}
          <div className="col-lg-9">
            {/* İlan Listesi */}
            <div className="row">
              {/* İlan kartları buraya gelecek */}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Index_login;
